import { type Dictionary } from '@ngrx/entity';

import {
  type CanvasElement,
  type CanvasElementFrom,
  type CanvasElementId,
  type CanvasElementType,
  type ColumnId,
} from '@blaze-runtime/engine-domain';
import {
  getFormGroupLikeWrapper,
  getWizardSummaryFormsIds,
  WrapperField,
} from '@blaze-runtime/engine-domain';

import { hasSlots, hasWrapperRefs } from '../../canvas-elements/element-model/element-props-guards';
import {
  isElementType,
  isFormGroupLike,
  isFormList,
  isVariantOfFormContainer,
  isWizard,
} from '../../canvas-elements/element-model/element-type-guards';
import { getWrapperRefs } from '../../canvas-elements/element-model/wrapper-refs';
import { type WizardStep } from '../../elements/wizard/wizard.models';
import { getWizardSteps, isWizardQuestionnaireMode } from '../../elements/wizard/wizard.utils';

export function pickParentElement(
  elements: Dictionary<CanvasElement>,
  elementId: string,
): CanvasElement | undefined {
  const parentId = elements[elementId]?.parentNode?.id;
  return parentId ? elements[parentId] : undefined;
}

export function isElementChildOf(
  elements: Dictionary<CanvasElement>,
  elementId: CanvasElementId,
  parentId: CanvasElementId,
): boolean {
  const parentElement = pickParentElement(elements, elementId);
  if (!parentElement) {
    return false;
  } else if (parentElement.id === parentId) {
    return true;
  }
  return isElementChildOf(elements, parentElement.id, parentId);
}

export function isElementChildOfAtSlot(
  elements: Dictionary<CanvasElement>,
  elementId: CanvasElementId,
  parentId: CanvasElementId,
  slotId: ColumnId,
): boolean {
  let currentElementId = elementId;
  let parentElement = pickParentElement(elements, currentElementId);
  while (parentElement) {
    if (parentElement.id === parentId) {
      return parentElement.slots?.[slotId]?.children.includes(currentElementId) ?? false;
    }
    currentElementId = parentElement.id;
    parentElement = pickParentElement(elements, currentElementId);
  }
  return false;
}

function pathFromParent(
  elements: Dictionary<CanvasElement>,
  elementId: CanvasElementId,
  parentId: CanvasElementId,
): CanvasElementId[] {
  const path: CanvasElementId[] = [];
  let id: CanvasElementId | undefined = elementId;
  while (id) {
    path.unshift(id);
    if (id === parentId) {
      break;
    }
    id = pickParentElement(elements, id)?.id;
  }
  return path;
}

export function isElementPreceding(
  elements: Dictionary<CanvasElement>,
  id1: CanvasElementId,
  id2: CanvasElementId,
  commonParent: CanvasElementId,
): boolean {
  const path1 = pathFromParent(elements, id1, commonParent);
  const path2 = pathFromParent(elements, id2, commonParent);
  let lastParentId: CanvasElementId = commonParent;
  for (let i = 0; i < Math.min(path1.length, path2.length); i++) {
    if (path1[i] === path2[i]) {
      lastParentId = path1[i];
    } else {
      const lastParent = elements[lastParentId];
      const children = lastParent?.slots?.ids.flatMap(
        (slotId) => lastParent?.slots?.[slotId]?.children ?? [],
      );
      const firstChild = children?.find((child) => [path1[i], path2[i]].includes(child));
      return firstChild === path1[i];
    }
  }
  throw new Error('invalid paths');
}

export function findParentForm(
  elements: Dictionary<CanvasElement>,
  elementId: CanvasElementId,
): CanvasElementId | undefined {
  const parentElement = pickParentElement(elements, elementId);
  if (!parentElement) {
    return undefined;
  } else if (isVariantOfFormContainer(parentElement)) {
    return parentElement.id;
  }
  return findParentForm(elements, parentElement.id);
}

export function findParentFormList(
  elements: Dictionary<CanvasElement>,
  elementId: CanvasElementId,
): CanvasElementId | undefined {
  const parentElement = pickParentElement(elements, elementId);
  if (!parentElement) {
    return undefined;
  } else if (isFormList(parentElement)) {
    return parentElement.id;
  }
  return findParentFormList(elements, parentElement.id);
}

export function findParentFormGroupLike(
  elements: Dictionary<CanvasElement>,
  elementId: CanvasElementId,
): CanvasElementFrom<'formGroup' | 'wizard'> | undefined {
  let currentElement = elements[elementId];
  while (currentElement) {
    if (
      isFormGroupLike(currentElement) &&
      hasWrapperRefs(currentElement) &&
      Object.values(currentElement.props.wrapper.refs).includes(elementId)
    ) {
      return currentElement;
    }
    currentElement = pickParentElement(elements, currentElement.id);
  }
  return undefined;
}

export function findParentFormGroupLikeOfForm(
  elements: Dictionary<CanvasElement>,
  elementId: CanvasElementId,
): CanvasElementFrom<'formGroup' | 'wizard'> | undefined {
  return Object.values(elements).find((element) => {
    if (!isFormGroupLike(element)) {
      return false;
    }
    const formIds = getFormGroupLikeWrapper(getWrapperRefs(element)).map(({ formId }) => formId);
    return formIds.includes(elementId);
  }) as CanvasElementFrom<'formGroup' | 'wizard'> | undefined;
}

/**
 * The questionnaire wizard step (question) whose slot holds the given form, or undefined
 * when the form does not belong to a questionnaire wizard.
 */
export function findWizardQuestionStepOfForm(
  elements: Dictionary<CanvasElement>,
  formId: CanvasElementId,
): WizardStep | undefined {
  const wizard = findParentFormGroupLikeOfForm(elements, formId);
  if (!isWizard(wizard) || !isWizardQuestionnaireMode(wizard.props)) {
    return undefined;
  }
  return getWizardSteps(wizard.props).find(
    ({ slotId }) => slotId && wizard.slots[slotId]?.children.includes(formId),
  );
}

/**
 * True for elements generated and owned by a questionnaire wizard's question: the
 * question's form, its generated field elements and the question/explanation Text
 * elements. Their lifecycle belongs to the wizard's Questions panel - the editor must not
 * delete, copy or drag them out. Elements the builder added into a question form manually
 * are not included.
 */
export function isQuestionnaireGeneratedElement(
  elements: Dictionary<CanvasElement>,
  elementId: CanvasElementId,
): boolean {
  const element = elements[elementId];
  if (!element) {
    return false;
  }
  if (isElementType(element, 'formContainer') && element.props.content.origin === 'wizard') {
    return !!findWizardQuestionStepOfForm(elements, elementId);
  }

  let parent = pickParentElement(elements, elementId);
  while (parent && !isElementType(parent, 'formContainer')) {
    parent = pickParentElement(elements, parent.id);
  }
  if (!isElementType(parent, 'formContainer') || parent.props.content.origin !== 'wizard') {
    return false;
  }
  const step = findWizardQuestionStepOfForm(elements, parent.id);
  if (!step) {
    return false;
  }
  if (step.questionElementId === elementId || step.explanationElementId === elementId) {
    return true;
  }
  return Object.entries(getWrapperRefs(parent)).some(
    ([refId, fieldId]) => WrapperField.isRefId(refId) && fieldId === elementId,
  );
}

export function findParentWizardOfSummary(
  elements: Dictionary<CanvasElement>,
  elementId: CanvasElementId,
): CanvasElementFrom<'wizard'> | undefined {
  return Object.values(elements).find((element) => {
    if (!isWizard(element)) {
      return false;
    }
    const detailsIds = getWizardSummaryFormsIds(getWrapperRefs(element)).map(
      ({ detailsId }) => detailsId,
    );
    return detailsIds.includes(elementId);
  }) as CanvasElementFrom<'wizard'> | undefined;
}

export function findParentByElementType(
  elements: Dictionary<CanvasElement>,
  elementId: CanvasElementId,
  elementType: CanvasElementType,
  checkCurrent = false,
): CanvasElement | undefined {
  const parentElement = !checkCurrent
    ? pickParentElement(elements, elementId)
    : elements[elementId];
  if (!parentElement) {
    return undefined;
  } else if (parentElement.type === elementType) {
    return parentElement;
  }
  return findParentByElementType(elements, parentElement.id, elementType);
}

/**
 * It works the same as findParentByElementTypes but it accepts multiple element types.
 * Copied for performance reasons.
 */
export function findParentByElementTypes(
  elements: Dictionary<CanvasElement>,
  elementId: CanvasElementId,
  elementTypes: CanvasElementType[],
  checkCurrent = false,
): CanvasElement | undefined {
  const parentElement = !checkCurrent
    ? pickParentElement(elements, elementId)
    : elements[elementId];
  if (!parentElement) {
    return undefined;
  } else if (elementTypes.includes(parentElement.type)) {
    return parentElement;
  }
  return findParentByElementTypes(elements, parentElement.id, elementTypes);
}

/** Returns all children of an element (recursively -> also children of children with no depth limit) */
export function getAllChildrenIds(
  entities: Dictionary<CanvasElement>,
  mainElementId: CanvasElementId | undefined,
): CanvasElementId[] {
  if (!mainElementId) {
    return [];
  }

  // Step 1 - gather element ids to update
  const elementIdsToUpdate: CanvasElementId[] = [];

  // Recursively visit all elements to populate
  function visitElement(element?: CanvasElement): void {
    if (element?.id === undefined) {
      return;
    }
    elementIdsToUpdate.push(element.id);

    // Go through slots
    if (!hasSlots(element)) {
      return;
    }
    for (const slotId of element.slots.ids) {
      const slot = element.slots[slotId];
      if (!slot) {
        continue;
      }

      for (const id of slot.children) {
        visitElement(entities[id]);
      }
    }
  }

  // Start with the main element
  visitElement(entities[mainElementId]);

  return elementIdsToUpdate;
}
